import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Listing, Pokemon } from '../lib/types';
import { Button, Modal, ShinyChip, TypeChip } from './ui';
import { ExpBar } from './game';
import { EggArt } from './EggArt';
import {
  dexNumber,
  formatDate,
  formatNumber,
  genderSuffix,
  STAT_LABELS,
  timeAgo,
  titleCase,
} from '../lib/format';

/**
 * One stall's worth of goods.
 *
 * The same card renders a Pokemon, an egg and a stack of items, because a
 * shopper is comparing *prices*, and three different layouts would make that
 * harder than it needs to be. What differs is only what sits in the well.
 */
export function ListingCard({
  listing,
  onBuy,
  onCancel,
  busy,
  showSeller = true,
}: {
  listing: Listing;
  onBuy?: (listing: Listing) => void;
  onCancel?: (listing: Listing) => void;
  busy?: boolean;
  showSeller?: boolean;
}) {
  const shiny = listing.pokemon?.isShiny ?? false;

  /**
   * Buying a Pokemon used to be a decision made from a sprite, a level and an
   * IV *total* — everything that actually distinguishes one Marill from another
   * (which stats those genes landed in, its size, its markings, how far its
   * EXP has come) was on the card's own page, and that page belongs to somebody
   * else. So the card opens.
   *
   * It is a modal rather than a hover panel because a stall grid is dense: a
   * panel that opens under the pointer covers the listings either side of it,
   * and on a phone there is no hover at all. The Grove can hover-to-open
   * because its sprites are sparse and feeding has to be one click; a market
   * is read, then decided on.
   *
   * Nothing new is fetched — the browse response already carries the whole
   * Pokemon, so opening a card costs no request and cannot fail.
   */
  const [detail, setDetail] = useState(false);
  const openable = listing.kind === 'POKEMON' && listing.pokemon !== null;

  return (
    <div className={`pxframe pxframe--raised stall ${shiny ? 'stall--shiny' : ''}`}>
      {shiny && <span className="stall__tag">SHINY</span>}

      <div className="stall__goods">
        {listing.kind === 'POKEMON' && listing.pokemon && (
          <img className="pixel" src={listing.pokemon.sprite} alt="" loading="lazy" />
        )}
        {listing.kind === 'EGG' && listing.egg && (
          <EggArt appearance={listing.egg.appearance} state="still" label={listing.egg.familyName} />
        )}
        {listing.kind === 'ITEM' && listing.item && (
          <span style={{ fontSize: '2.6rem' }} aria-hidden>
            ◆
          </span>
        )}
      </div>

      {openable ? (
        // The name is the handle, so the whole card is not one giant button —
        // the Buy button and the seller link inside it have their own jobs.
        <button type="button" className="stall__open" onClick={() => setDetail(true)}>
          <span className="stall__name">{title(listing)}</span>
          <span className="stall__more">View details</span>
        </button>
      ) : (
        <div className="stall__name">{title(listing)}</div>
      )}
      <div className="stall__meta">{subtitle(listing)}</div>
      <BreedingChips listing={listing} />

      {listing.note && (
        <div className="tiny muted" style={{ fontStyle: 'italic' }}>
          "{listing.note}"
        </div>
      )}

      <div className="row row--between" style={{ marginTop: 'auto' }}>
        <span className="stall__price">{formatNumber(listing.unitPrice)}</span>
        {listing.kind === 'ITEM' && <span className="stall__qty">x{listing.quantity}</span>}
      </div>

      {showSeller && (
        <Link className="stall__seller" to={`/bazaar/${listing.seller.username}`}>
          {listing.seller.bazaarName}
        </Link>
      )}

      <div className="row row--between">
        <span className="tiny muted" style={{ fontSize: '0.5rem' }}>
          ends {timeAgo(listing.expiresAt)}
        </span>
        {onBuy && (
          <Button size="sm" variant="gold" disabled={busy} loading={busy} onClick={() => onBuy(listing)}>
            Buy
          </Button>
        )}
        {onCancel && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onCancel(listing)}>
            Take down
          </Button>
        )}
      </div>

      {detail && listing.pokemon && (
        <ListingDetail
          listing={listing}
          pokemon={listing.pokemon}
          busy={busy}
          onBuy={onBuy}
          onClose={() => setDetail(false)}
        />
      )}
    </div>
  );
}

/**
 * Everything about one Pokemon on a stall, and the price beside it.
 *
 * Deliberately the *seller's* view of the Pokemon rather than the owner's: no
 * rename, no feed, no release. What it adds over the card is the two things a
 * buyer cannot otherwise see — where the genes actually landed, and the
 * provenance behind the chips.
 */
function ListingDetail({
  listing,
  pokemon,
  busy,
  onBuy,
  onClose,
}: {
  listing: Listing;
  pokemon: Pokemon;
  busy?: boolean;
  onBuy?: (listing: Listing) => void;
  onClose: () => void;
}) {
  const info = listing.breeding;
  const gender = genderSuffix(pokemon.displayName, pokemon.gender);

  return (
    <Modal onClose={onClose} labelledBy="listing-detail-name" wide>
      <div className="stack">
        <div className="row row--wrap" style={{ gap: 'var(--gap)' }}>
          <img
            className="pixel"
            src={pokemon.sprite}
            alt={pokemon.displayName}
            style={{ width: 128, height: 128, objectFit: 'contain' }}
          />
          <div className="stack-sm" style={{ flex: 1, minWidth: 200 }}>
            <div className="row row--wrap">
              <h2 id="listing-detail-name" style={{ fontSize: 'var(--text-xl)' }}>
                {pokemon.displayName}
              </h2>
              {pokemon.isShiny && <ShinyChip />}
            </div>
            <div className="tiny muted mono">
              {dexNumber(pokemon.species.id)} {pokemon.species.displayName}
            </div>
            <div className="row row--wrap">
              {pokemon.species.types.map((type) => (
                <TypeChip key={type} type={type} />
              ))}
              {pokemon.nature && <span className="chip">{pokemon.nature.name}</span>}
              {gender && <span className="chip">{gender}</span>}
            </div>
          </div>
        </div>

        <ExpBar pokemon={pokemon} />

        <hr className="divider" />

        {/* The same rows the Pokemon's own page draws: the bar is the gene, the
            number on the right is what that gene is worth at this level. An IV
            total alone cannot tell a buyer whether 31 landed in Speed or in the
            stat this species already has too much of. */}
        <div className="stack-sm">
          {(Object.keys(STAT_LABELS) as Array<keyof typeof pokemon.stats>).map((key) => {
            const iv = pokemon.ivs[key];
            return (
              <div key={key} className="stat-row">
                <span className="stat-row__name">
                  {STAT_LABELS[key]}
                  {pokemon.nature?.increased === key && (
                    <span style={{ color: 'var(--leaf-dark)' }}> ▲</span>
                  )}
                  {pokemon.nature?.decreased === key && (
                    <span style={{ color: 'var(--berry-dark)' }}> ▼</span>
                  )}
                </span>
                <div className="stat-row__track" title={`IV ${iv}/31`}>
                  <div
                    className={`stat-row__fill ${iv >= 26 ? 'stat-row__fill--high' : iv <= 8 ? 'stat-row__fill--low' : ''}`}
                    style={{ width: `${(iv / 31) * 100}%` }}
                  />
                </div>
                <span className="stat-row__value">{pokemon.stats[key]}</span>
              </div>
            );
          })}
        </div>
        <div className="tiny muted mono">IV total {pokemon.ivTotal} / 186</div>

        <hr className="divider" />

        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
          <Fact label="Nature" value={pokemon.nature?.name ?? '—'} />
          <Fact label="Height" value={`${(pokemon.species.height / 10).toFixed(1)} m`} />
          <Fact label="Weight" value={`${(pokemon.species.weight / 10).toFixed(1)} kg`} />
          <Fact
            label="Egg groups"
            value={pokemon.species.eggGroups.map((group) => group.replace('-', ' ')).join(', ') || '—'}
          />
          {info && <Fact label="Generation" value={String(info.generation)} />}
          {info?.sizeClass && <Fact label="Size" value={sizeLabel(info.sizeClass)} />}
          {info?.marking && <Fact label="Marking" value={titleCase(info.marking)} />}
          {info?.formKey && <Fact label="Form" value={titleCase(info.formKey)} />}
          {info && info.shinyHeritage > 0 && (
            <Fact label="Shiny heritage" value={info.shinyHeritageLabel} />
          )}
          {info?.breederName && <Fact label="Bred by" value={info.breederName} />}
          {info?.breedingLevel != null && info.breedingLevel > 1 && (
            <Fact label="Breeder level" value={String(info.breedingLevel)} />
          )}
          <Fact label="Listed" value={formatDate(listing.createdAt)} />
        </div>

        {listing.note && (
          <div className="pxwell tiny" style={{ fontStyle: 'italic' }}>
            "{listing.note}"
          </div>
        )}

        <hr className="divider" />

        <div className="row row--between row--wrap">
          <div className="stack-sm" style={{ gap: 0 }}>
            <span className="stall__price">{formatNumber(listing.unitPrice)}</span>
            <span className="tiny muted">
              from{' '}
              <Link to={`/bazaar/${listing.seller.username}`} onClick={onClose}>
                {listing.seller.bazaarName}
              </Link>{' '}
              · ends {timeAgo(listing.expiresAt)}
            </span>
          </div>
          <div className="row">
            <Button size="sm" variant="ghost" onClick={onClose}>
              Close
            </Button>
            {onBuy && (
              <Button
                size="sm"
                variant="gold"
                disabled={busy}
                loading={busy}
                onClick={() => {
                  onBuy(listing);
                  onClose();
                }}
              >
                Buy
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="tiny muted mono" style={{ fontSize: '0.5625rem', letterSpacing: '0.08em' }}>
        {label.toUpperCase()}
      </div>
      <div className="mono tiny">{value}</div>
    </div>
  );
}

/** The Bazaar's own filter wording, so a card and the filter that found it agree. */
function sizeLabel(sizeClass: string): string {
  if (sizeClass === 'XXL') return 'XXL (Titan)';
  if (sizeClass === 'XXS') return 'XXS (Miniature)';
  if (sizeClass === 'M') return 'Average';
  if (sizeClass === 'S') return 'Small';
  if (sizeClass === 'L') return 'Large';
  return sizeClass;
}

/**
 * The breeding chips on a Pokemon listing — design PART 24's lineage card.
 *
 * Only what is *unusual* is shown. An Average-sized Generation 0 Pokemon with
 * no marking gets no chips at all, because a row of "Average / Gen 0 / none" on
 * every card is noise that hides the one listing that actually is a Generation
 * 19 Titan.
 */
function BreedingChips({ listing }: { listing: Listing }) {
  const info = listing.breeding;
  if (!info) return null;

  const chips: string[] = [];
  if (info.sizeClass === 'XXL') chips.push('Titan');
  else if (info.sizeClass === 'XXS') chips.push('Miniature');
  else if (info.sizeClass && !['M'].includes(info.sizeClass)) chips.push(info.sizeClass);
  if (info.marking) chips.push(titleCase(info.marking));
  if (info.generation > 0) chips.push(`Gen ${info.generation}`);
  if (info.shinyHeritage >= 3) chips.push('Shiny line');
  if ((info.breedingLevel ?? 1) >= 20) chips.push(`Breeder Lv ${info.breedingLevel}`);

  if (chips.length === 0 && !info.breederName) return null;

  return (
    <div className="row row--wrap tiny" style={{ gap: 3 }}>
      {chips.map((chip) => (
        <span key={chip} className="chip chip--sm">
          {chip}
        </span>
      ))}
      {info.breederName && <span className="tiny muted">bred by {info.breederName}</span>}
    </div>
  );
}

function title(listing: Listing): string {
  if (listing.kind === 'POKEMON' && listing.pokemon) return listing.pokemon.displayName;
  if (listing.kind === 'EGG' && listing.egg) return listing.egg.familyName;
  return listing.item?.name ?? 'Something';
}

function subtitle(listing: Listing): string {
  if (listing.kind === 'POKEMON' && listing.pokemon) {
    const mon = listing.pokemon;
    const gender = mon.gender === 'male' ? '♂' : mon.gender === 'female' ? '♀' : '';
    return [`Lv ${mon.level}`, mon.nature?.name, gender, `IV ${mon.ivTotal}/186`]
      .filter(Boolean)
      .join(' · ');
  }
  if (listing.kind === 'EGG' && listing.egg) {
    // Never the contents — only how far along it is.
    return `${listing.egg.progress} / ${listing.egg.requiredProgress} steps warmed`;
  }
  return `${listing.quantity} available`;
}
